import { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import { MessageTestingProgressBar } from '@trinity/chat-components/components/MessageTestingGrid/ProgressBar.js';
import { MessageTestingResultsTabs } from '@trinity/chat-components/components/MessageTestingGrid/ResultsTabs.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useDownloader } from '@trinity/chat-components/hooks/useDownloader';
import { useMessageTestingRun } from '@trinity/chat-components/hooks/useMessageTestingRun';
import type { SegmentOption, TestableMessage } from './types';

interface MessageTestingResultsPanelProps {
  runIdentifier: string;
  onClose: () => void;
  messages: TestableMessage[];
  segments: SegmentOption[];
  brand?: string;
  objective?: string;
}

export function MessageTestingResultsPanel({
  runIdentifier,
  onClose,
  messages,
  segments,
  brand,
  objective,
}: MessageTestingResultsPanelProps): React.JSX.Element {
  const apiClient = useChatApiClient();
  const download = useDownloader();
  const { status, progress, cells, cellErrors, error, rollups } = useMessageTestingRun(runIdentifier);
  const [exporting, setExporting] = useState(false);

  const handleExportPpt = useCallback(async () => {
    setExporting(true);
    try {
      const blob = await apiClient.messageTesting.exportPpt(runIdentifier);
      download({ blob, filename: 'message_insights.pptx' });
    } catch {
      // Silently fail — the user can retry
    } finally {
      setExporting(false);
    }
  }, [apiClient, runIdentifier, download]);

  return (
    <Root>
      <PanelHeader>
        <PanelTitle>Message Testing Results</PanelTitle>
        <HeaderActions>
          {(status === 'analyzing' || status === 'completed') && (
            <ExportButton onClick={handleExportPpt} disabled={exporting}>
              {exporting ? 'Exporting…' : 'Download PPT'}
            </ExportButton>
          )}
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </HeaderActions>
      </PanelHeader>

      <MessageTestingProgressBar status={status} progress={progress} error={error} />

      {(brand || objective) && (
        <RunMeta>
          {brand && (
            <RunMetaItem>
              <strong>Brand:</strong> {brand}
            </RunMetaItem>
          )}
          {objective && (
            <RunMetaItem>
              <strong>Objective:</strong> {objective}
            </RunMetaItem>
          )}
        </RunMeta>
      )}

      {(cells.length > 0 || messages.length > 0) && (
        <MessageTestingResultsTabs
          messages={messages}
          segments={segments}
          cells={cells}
          cellErrors={cellErrors}
          rollups={rollups}
        />
      )}
    </Root>
  );
}

// ─── Panel-specific styled components ───────────────────────────────────────

const Root = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 6px 2px rgba(60, 64, 67, 0.15);
  height: 100%;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
`;

const PanelTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  color: #1a1a1a;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  color: white;
  background: #6366f1;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #4f46e5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  padding: 0 4px;

  &:hover {
    color: #1a1a1a;
  }
`;

const RunMeta = styled.div`
  padding: 10px 16px;
  display: flex;
  gap: 20px;
  font-size: 13px;
  color: #333;
  background: #f5f7fa;
  border-bottom: 1px solid #e0e0e0;
`;

const RunMetaItem = styled.span`
  strong {
    color: #555;
    margin-right: 4px;
  }
`;
